import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { MessageSquare, ArrowRight, ChevronDown } from 'lucide-react';
import { SearchParam } from '@common/ts/enums';
import { Button } from '@/components/ui/button';
import useResponsesPanelStore from '@/store/ResponsesPanelStore';
import useTranscriptPanelStore from '@/store/TranscriptPanelStore';
import useCategoryStore from '@/store/CategoryStore';
import useTranslation from '@/hooks/useTranslation';
import { useGetInterviewQuestionTranscriptsPaginated } from '@/reactQuery/get';
import type { CategoryApiResponse, SelectedCategory } from '@/types';
import { ResponseCard } from '../ResponsesPanel';

interface ResponsesZoneProps {
  questionId: string;
  categoryData?: CategoryApiResponse;
}

// Page sizes:
// - When no filter is active, the zone is a glanceable preview of
//   the first 5 responses + a "Show all" CTA — its original
//   purpose in the redesign.
// - When the filter IS active, we shift to a paginated 10-per-page
//   view inline so researchers can see the filter result in place
//   without being forced into the slide-out drawer (TELV1-292).
const PAGE_SIZE_PREVIEW = 5;
const PAGE_SIZE_FILTERED = 10;

/**
 * Inline zone surfacing the respondent answers directly in the
 * Questions main column (between StoryZone and EvidenceZone).
 *
 * Behaviour depends on whether the inline category filter is active:
 *
 *   • No filter — read-only preview of the first 5 responses with
 *     a "Show all" CTA that opens the slide-out ResponsesPanel for
 *     full search / sort / pagination. This is the original design
 *     intent of the zone.
 *
 *   • Filter active — paginated 10-per-page view inline, with a
 *     chip filter row above the cards. The CTA changes to
 *     "Search & sort →" to signal the drawer is for advanced
 *     controls, not the only way to filter. This restores the
 *     pre-redesign "filter answers in place" workflow that
 *     researchers were missing after TELV1-201.
 *
 * The filter state lives in `useResponsesPanelStore` so the inline
 * chips, the chart bars (additive toggle, see CategoriesChart) and
 * the chips inside the slide-out panel are all reading and writing
 * the same selection — they stay in sync regardless of which
 * surface the user touches.
 *
 * `QuestionItem` calls `resetForQuestion(questionId)` on question
 * change, so stale filters never bleed across questions even
 * though the store is global.
 */
function ResponsesZone({ questionId, categoryData }: ResponsesZoneProps) {
  const { lang } = useTranslation();
  const { project_id: projectId } = useParams();
  const [searchParams] = useSearchParams();
  const filterId = searchParams.get(SearchParam.FILTER_ID);
  const translationLanguage = searchParams.get(SearchParam.TRANSLATION_LANGUAGE);
  const languageParam =
    !translationLanguage || translationLanguage === 'Original' ? null : translationLanguage;
  const categoryType = useCategoryStore((state) => state.categoryType);

  const openResponsesPanel = useResponsesPanelStore((s) => s.open);
  const openTranscriptPanel = useTranscriptPanelStore((s) => s.open);
  const selectedCategories = useResponsesPanelStore((s) => s.selectedCategories);
  const setSelectedCategories = useResponsesPanelStore((s) => s.setSelectedCategories);
  const clearCategories = useResponsesPanelStore((s) => s.clearCategories);

  const isFilterActive = selectedCategories.length > 0;
  const pageSize = isFilterActive ? PAGE_SIZE_FILTERED : PAGE_SIZE_PREVIEW;

  // ── Chip options ────────────────────────────────────────────────
  // Derived from the authoritative categoryData prop (not from the
  // current page of transcripts) so the chip row is complete and
  // stable across pages. Matches the panel's allCategoryOptions
  // derivation — same labels, same alphabetical sort.
  const allCategoryOptions = useMemo<SelectedCategory[]>(() => {
    if (!categoryData) return [];
    const pool = [...(categoryData.categories || []), ...(categoryData.categories_multi || [])];
    const seen = new Map<string, SelectedCategory>();
    pool.forEach((c) => {
      if (c?.label && !seen.has(c.label)) {
        seen.set(c.label, { label: c.label, value: c.label });
      }
    });
    return Array.from(seen.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [categoryData]);

  // ── Label → ID resolution ───────────────────────────────────────
  // The server filters by IDs (stable across label edits and
  // translations) but the store + UI track labels (what the user
  // sees). Same translation logic as ResponsesPanel.
  const categoryIds = useMemo<string[]>(() => {
    if (!selectedCategories.length || !categoryData) return [];
    const pool = [...(categoryData.categories || []), ...(categoryData.categories_multi || [])];
    const labelToId = new Map<string, string>();
    pool.forEach((c) => {
      if (c?.label && c?.id && !labelToId.has(c.label)) {
        labelToId.set(c.label, c.id.toString());
      }
    });
    return selectedCategories.map((s) => labelToId.get(s.label)).filter((id): id is string => !!id);
  }, [selectedCategories, categoryData]);

  // ── Pagination state ────────────────────────────────────────────
  const [currentPage, setCurrentPage] = useState(1);

  // Reset to page 1 when the filter or question changes so a user
  // on page 5 of 30 results who applies a filter down to 4 results
  // doesn't see "page 5 of 1".
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategories, questionId]);

  // ── Data fetch ──────────────────────────────────────────────────
  const { data: paginatedResponse } = useGetInterviewQuestionTranscriptsPaginated({
    filterId,
    categoryType,
    projectId,
    questionId,
    language: languageParam,
    page: currentPage,
    limit: pageSize,
    categoryIds: categoryIds.length ? categoryIds : undefined,
    sortBy: 'respondent_index',
    sortDirection: 'asc',
  });

  const transcripts = paginatedResponse?.data ?? [];
  const pagination = paginatedResponse?.pagination;
  const totalResponses = pagination?.totalItems ?? 0;
  const totalPages = pagination?.totalPages ?? 1;

  // Unfiltered total for the question — drives whether to render
  // the zone at all and what's shown next to "Respondent answers".
  // Comes from the categories endpoint which the parent already
  // fetched, so we don't pay for a second request.
  const unfilteredTotal = categoryData?.conversations_num ?? 0;

  // Hide the zone entirely only when the question genuinely has no
  // responses AND no filter is active. With a filter applied we
  // keep the zone visible (even at 0 matches) so the user has the
  // chip row and a "Clear filter" affordance — otherwise the zone
  // would vanish on a no-match filter, leaving the user wondering
  // where the "Answers" section went.
  if (unfilteredTotal === 0 && !isFilterActive) return null;

  const handleToggleChip = (opt: SelectedCategory) => {
    const exists = selectedCategories.some((c) => c.label === opt.label);
    setSelectedCategories(
      exists
        ? selectedCategories.filter((c) => c.label !== opt.label)
        : [...selectedCategories, opt],
    );
  };

  // CTA label: switches to "Search & sort →" when the filter is
  // active so users understand the drawer offers additional
  // capabilities (search, sort, larger view) — not a way to "see
  // more results" which is already happening inline via pagination.
  const ctaLabel = isFilterActive
    ? lang.get('msg.searchAndSort') || 'Search & sort'
    : lang.get('msg.showAllNResponses')?.replace('{n}', String(totalResponses)) ||
      `Show all ${totalResponses} responses`;

  return (
    <section className="flex flex-col gap-3">
      {/* Zone header: title + count left, CTA right */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-5 w-5 text-primary" />
          <h3 className="text-[15px] font-semibold text-neutral-900">
            {lang.get('msg.respondentAnswers') || 'Respondent answers'}
          </h3>
          <span className="text-sm font-normal text-neutral-400">
            {isFilterActive && unfilteredTotal !== totalResponses
              ? `(${totalResponses} / ${unfilteredTotal})`
              : `(${totalResponses})`}
          </span>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => openResponsesPanel(questionId)}
          className="text-xs h-8 shrink-0"
        >
          {ctaLabel}
          <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
        </Button>
      </div>

      {/* Chip filter row — only rendered when the question actually
          has categories. Matches the panel's chip styling so the
          two surfaces feel consistent; the "Clear" affordance gives
          a one-click escape if the filter goes empty. */}
      {allCategoryOptions.length > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[11px] font-medium text-neutral-500 mr-1 shrink-0">
            {lang.get('msg.filterByCategory')}:
          </span>
          {allCategoryOptions.map((opt) => {
            const isSelected = selectedCategories.some((c) => c.label === opt.label);
            return (
              <button
                key={opt.label}
                type="button"
                onClick={() => handleToggleChip(opt)}
                aria-pressed={isSelected}
                className={`text-[11px] font-medium px-2.5 py-1 rounded-full border transition-colors ${
                  isSelected
                    ? 'bg-primary/5 border-primary/30 text-primary'
                    : 'bg-white border-neutral-200 text-neutral-600 hover:border-neutral-300'
                }`}
              >
                {opt.label}
              </button>
            );
          })}
          {isFilterActive && (
            <button
              type="button"
              onClick={clearCategories}
              className="text-[11px] text-neutral-500 hover:text-neutral-700 underline ml-1"
            >
              {lang.get('msg.clearFilter') || 'Clear'}
            </button>
          )}
        </div>
      )}

      {/* Empty state when the filter has no matches. We keep the
          zone visible (vs. the unfiltered-empty case which hides
          it entirely above) and offer a clear "Clear filter" CTA
          so the user can recover without hunting for the X on a
          chip. */}
      {totalResponses === 0 && isFilterActive && (
        <div className="text-sm text-neutral-500 py-4">
          {lang.get('msg.noMatchingResponses') || 'No responses match the selected categories.'}
          <button
            type="button"
            onClick={clearCategories}
            className="text-primary hover:underline ml-2"
          >
            {lang.get('msg.clearFilter') || 'Clear filter'}
          </button>
        </div>
      )}

      {/* Cards */}
      {transcripts.length > 0 && (
        <div className="flex flex-col gap-3">
          {transcripts.map((transcript, idx) => (
            <ResponseCard
              key={transcript.conversation_id}
              transcript={transcript}
              // Respondent ordinal relative to the full filtered set
              // (not the page slice) — keeps "Respondent #37" stable
              // as #37 on page 2 at limit 10.
              index={(currentPage - 1) * pageSize + idx}
              categoryData={categoryData}
              questionId={questionId}
              projectId={projectId}
              onOpenTranscript={openTranscriptPanel}
            />
          ))}
        </div>
      )}

      {/* Inline pagination — only when the filter is active AND
          there's more than one page. The unfiltered preview keeps
          its "first 5 then Show all" feel; we don't paginate that
          inline. */}
      {isFilterActive && totalPages > 1 && (
        <div className="flex items-center justify-between gap-4 mt-2 pt-3 border-t border-neutral-100">
          <span className="text-xs text-neutral-500">
            {totalResponses} {lang.get('msg.responses') || 'responses'}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="h-8 w-8 p-0"
              disabled={!pagination?.hasPreviousPage}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              aria-label="Previous page"
            >
              <ChevronDown className="h-4 w-4 rotate-90" />
            </Button>
            <span className="text-xs text-neutral-600 tabular-nums">
              {currentPage} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              className="h-8 w-8 p-0"
              disabled={!pagination?.hasNextPage}
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              aria-label="Next page"
            >
              <ChevronDown className="h-4 w-4 -rotate-90" />
            </Button>
          </div>
        </div>
      )}

      {/* Footer "Show all" CTA — only when there is NO filter and
          there are more responses than the preview shows. With a
          filter active the inline pagination handles "more" so we
          don't double up. */}
      {!isFilterActive && totalResponses > transcripts.length && (
        <button
          type="button"
          onClick={() => openResponsesPanel(questionId)}
          className="self-start inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:text-darker-primary transition-colors"
        >
          {lang.get('msg.showAllNResponses')?.replace('{n}', String(totalResponses)) ||
            `Show all ${totalResponses} responses`}
          <ArrowRight className="h-3.5 w-3.5" />
        </button>
      )}
    </section>
  );
}

export default ResponsesZone;
